import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Avatar,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Paper
} from '@mui/material';
import {
  LocationOn,
  AttachMoney,
  CalendarToday,
  Visibility,
  Assignment,
  NoteAdd,
  Person,
  AutoAwesome,
  Edit,
  Description as DescriptionIcon,
  Chat
} from '@mui/icons-material';
import messagingService from '../services/messagingService';
import AIMilestoneCreator from '../components/milestones/AIMilestoneCreator';
import MilestoneCreationDialog from '../components/milestones/MilestoneCreationDialog';
import MilestoneManagerWithReports from '../components/milestones/MilestoneManagerWithReports';
import AddDailyEntryDialog from '../components/projects/AddDailyEntryDialog';
import DialogCloseButton from '../components/common/DialogCloseButton';
import { brand, text, border } from '../theme/colors';

const extractMilestones = (payload) => {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const ContractorProjectsPage = () => {
  const { user, getApiClient } = useAuth();
  const navigate = useNavigate();
  const { id: routeProjectId } = useParams();
  const [searchParams] = useSearchParams();

  // State management
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectDetail, setShowProjectDetail] = useState(false);
  const [dailyUpdateProject, setDailyUpdateProject] = useState(null);
  const [createMilestoneMenuAnchor, setCreateMilestoneMenuAnchor] = useState(null);
  const [showAIMilestoneCreator, setShowAIMilestoneCreator] = useState(false);
  const [showManualMilestoneDialog, setShowManualMilestoneDialog] = useState(false);
  const [stageRefreshKey, setStageRefreshKey] = useState(0);
  const targetProjectId = routeProjectId || searchParams.get('project');

  const loadProjects = useCallback(async ({ showSpinner = true } = {}) => {
    try {
      if (showSpinner) setLoading(true);
      setError(null);
      console.log('Loading contractor projects for user:', user?.id);
      
      if (!user?.id) {
        setProjects([]);
        return [];
      }
      
      // Fetch contractor's assigned projects from API
      const apiClient = getApiClient();
      const response = await apiClient.get('/projects');
      
      let fetchedProjects = [];
      if (response.data?.success && response.data?.data?.projects) {
        fetchedProjects = response.data.data.projects;
      } else if (Array.isArray(response.data)) {
        fetchedProjects = response.data;
      } else if (response.data?.projects) {
        fetchedProjects = response.data.projects;
      }
      
      // Filter to only show projects assigned to this contractor (not open_for_bids)
      const assignedProjects = fetchedProjects
        .filter(p => p.contractor_id === user.id || p.contractorId === user.id)
        .map(project => {
          // Ensure each project has a client object with safe defaults
          const normalizedProject = { ...project };
          if (!normalizedProject.client) {
            normalizedProject.client = {
              id: project.owner_id || '',
              name: 'Unknown Project Owner',
              email: '',
              phone: ''
            };
          }
          // Ensure milestones array exists
          if (!normalizedProject.milestones || !Array.isArray(normalizedProject.milestones)) {
            normalizedProject.milestones = [];
          }
          // Ensure other arrays exist
          if (!normalizedProject.payments || !Array.isArray(normalizedProject.payments)) {
            normalizedProject.payments = [];
          }
          if (!normalizedProject.documents || !Array.isArray(normalizedProject.documents)) {
            normalizedProject.documents = [];
          }
          return normalizedProject;
        });

      const assignedProjectsWithStages = await Promise.all(
        assignedProjects.map(async (project) => {
          try {
            const milestonesResponse = await apiClient.get(`/projects/${project.id}/milestones`);
            return {
              ...project,
              milestones: extractMilestones(milestonesResponse.data)
            };
          } catch (milestoneErr) {
            console.error(`Error loading work stages for project ${project.id}:`, milestoneErr);
            return project;
          }
        })
      );
      
      console.log(`Loaded ${assignedProjectsWithStages.length} assigned projects for contractor`);
      setProjects(assignedProjectsWithStages);
      return assignedProjectsWithStages;
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Failed to load projects. Please try again.');
      setProjects([]);
      return [];
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [user?.id, getApiClient]);

  // Load projects data
  useEffect(() => {
    if (user?.id) {
      loadProjects();
    }
  }, [user?.id, loadProjects]);

  const targetProject = useMemo(() => {
    if (!targetProjectId) return null;
    return projects.find(project => String(project.id) === String(targetProjectId));
  }, [projects, targetProjectId]);

  useEffect(() => {
    if (!targetProjectId || loading) return;

    if (targetProject) {
      setSelectedProject(targetProject);
      setShowProjectDetail(true);
      return;
    }

    if (projects.length > 0) {
      setError('Project not found in your active projects.');
    }
  }, [targetProjectId, targetProject, loading, projects.length]);

  const getStatusColor = (status) => {
    const colors = {
      'completed': 'success',
      'in_progress': 'primary',
      'pending': 'warning',
      'overdue': 'error',
      'planning': 'info',
      'open_for_bids': 'default'
    };
    return colors[status] || 'default';
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid Date';
    }
  };

  const getStageWorkflowState = (project) => {
    const stages = project?.milestones || [];
    const completedStages = stages.filter(stage => ['completed', 'verified', 'released'].includes(stage.status)).length;

    if (project?.status === 'completed') {
      return {
        stepLabel: 'Final handover complete',
        title: 'Project complete',
        description: 'Review final documentation, payment history, and owner messages if anything needs follow-up.',
        primaryAction: 'View Work Stages',
        primaryType: 'stages',
      };
    }

    if (stages.length === 0) {
      return {
        stepLabel: 'Step 1 of 4',
        title: 'Create and send your Stage Plan',
        description: 'First ask the owner how they want to fund the job — everything at once, or stage by stage. Then break the job into stages sized to what they can fund at a time (a small job can be one stage), add timeline and amounts, and share the plan for approval. Each stage is funded into escrow before you start it, so you always know the money is there.',
        primaryAction: 'Create Stage Plan',
        primaryType: 'createStagePlan',
      };
    }

    if (completedStages < stages.length) {
      return {
        stepLabel: `Step 3 of 4 • ${completedStages}/${stages.length} stages complete`,
        title: 'Work through the approved stages',
        description: 'For each stage, confirm the BOQ, complete the work, upload photos and costs, then submit a Completion Report so the owner can approve release of funds.',
        primaryAction: 'Open Stage BOQ',
        primaryType: 'boq',
      };
    }

    return {
      stepLabel: 'Step 4 of 4',
      title: 'Submit the final handover report',
      description: 'Summarize the completed work, attach final photos and certificates, sign off the project, and request the final payment release.',
      primaryAction: 'Open Stage Reports',
      primaryType: 'boq',
    };
  };

  const handleViewProject = (project) => {
    setSelectedProject(project);
    setShowProjectDetail(true);
  };

  const handleCloseProjectDetail = () => {
    setShowProjectDetail(false);
    if (targetProjectId) {
      navigate('/projects', { replace: true });
    }
  };

  const handleCreateMilestoneClick = (event) => {
    setCreateMilestoneMenuAnchor(event.currentTarget);
  };

  const handleCreateMilestoneMenuClose = () => {
    setCreateMilestoneMenuAnchor(null);
  };

  const handleOpenAIMilestoneCreator = () => {
    handleCreateMilestoneMenuClose();
    setShowAIMilestoneCreator(true);
  };

  const handleOpenManualMilestoneDialog = () => {
    handleCreateMilestoneMenuClose();
    setShowManualMilestoneDialog(true);
  };

  const handlePrimaryStageAction = (project) => {
    const workflow = getStageWorkflowState(project);
    if (workflow.primaryType === 'createStagePlan') {
      setShowManualMilestoneDialog(true);
      return;
    }
    setShowProjectDetail(false);
    navigate(`/projects/${project.id}/boq`);
  };

  const handleMessageUser = async (userId) => {
    try {
      const res = await messagingService.getOrCreateConversation(userId);
      const convId = res.data?.id;
      navigate(convId ? `/messages?conversation=${convId}` : '/messages');
    } catch {
      navigate('/messages');
    }
  };

  const handleOpenDailyUpdateDialog = (project) => {
    setDailyUpdateProject(project);
  };

  const handleDailyEntryCreated = async () => {
    setDailyUpdateProject(null);
    setSuccess('Daily update posted successfully!');
    setTimeout(() => setSuccess(''), 4000);
    await loadProjects({ showSpinner: false });
  };

  const handleMilestoneCreated = async () => {
    setShowAIMilestoneCreator(false);
    setShowManualMilestoneDialog(false);
    setSuccess('Work stage created successfully!');
    setStageRefreshKey(prev => prev + 1);

    const refreshedProjects = await loadProjects({ showSpinner: false });
    const refreshedProject = refreshedProjects.find(project => String(project.id) === String(selectedProject?.id));
    let refreshedStages = [];

    if (selectedProject?.id) {
      try {
        const apiClient = getApiClient();
        const response = await apiClient.get(`/projects/${selectedProject.id}/milestones`);
        refreshedStages = response.data?.data || response.data || [];
      } catch (err) {
        console.error('Error refreshing work stages:', err);
      }
    }

    const nextSelectedProject = refreshedProject || selectedProject;
    if (nextSelectedProject) {
      const projectWithStages = {
        ...nextSelectedProject,
        milestones: Array.isArray(refreshedStages) ? refreshedStages : nextSelectedProject.milestones || [],
      };
      setSelectedProject(projectWithStages);
      setProjects(prev => prev.map(project => (
        String(project.id) === String(projectWithStages.id) ? projectWithStages : project
      )));
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading your projects...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          My Projects
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your assigned projects and track progress
        </Typography>
      </Box>

      {/* Projects Grid */}
      {projects.length === 0 ? (
        <Card sx={{ py: 8, textAlign: 'center' }}>
          <CardContent>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No projects assigned
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto', mb: 3 }}>
              Your won projects will appear here. Start by finding work, submitting bids, and building a portfolio clients can trust.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
              <Button
                variant="contained"
                onClick={() => navigate('/contractor/marketplace')}
                sx={{ background: brand.gradient, textTransform: 'none', fontWeight: 700 }}
              >
                Find Work
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/contractor/portfolio')}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Build Portfolio
              </Button>
              <Button
                variant="text"
                onClick={() => navigate('/bids')}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                View Bids
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {projects.map((project) => (
            <Grid item xs={12} md={6} key={project.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 3, border: `1px solid ${border.light}`, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', '&:hover': { boxShadow: '0 4px 16px rgba(102,126,234,0.15)' }, transition: 'box-shadow 0.2s' }}>
                <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                  {/* Top row: Status + Work Stages + Client */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip
                        label={project.status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
                        color={getStatusColor(project.status)}
                        size="small"
                      />
                      <Chip
                        label={`${(project.milestones || []).length} Stages`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Avatar src={project.client?.avatar} sx={{ width: 24, height: 24, fontSize: 12 }}>
                        {project.client?.name?.[0] || 'C'}
                      </Avatar>
                      <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {project.client?.name || 'Client'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Title */}
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.5 }}>
                    {project.title}
                  </Typography>

                  {/* Description — compact */}
                  {project.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: 1.4
                      }}
                    >
                      {project.description}
                    </Typography>
                  )}

                  {/* Details — 2×2 grid */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2, p: 1.5, bgcolor: '#f8f9fb', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <LocationOn sx={{ fontSize: 16, color: brand.primary }} />
                      <Typography variant="caption" sx={{ color: '#555' }}>
                        {project.location || 'Not specified'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <AttachMoney sx={{ fontSize: 16, color: brand.primary }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, color: text.primary }}>
                        {formatCurrency(project.budget)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <CalendarToday sx={{ fontSize: 16, color: brand.primary }} />
                      <Typography variant="caption" sx={{ color: '#555' }}>
                        {formatDate(project.assignedDate || project.created_at)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Chip
                        label={`${(project.milestones || []).filter(m => m.status === 'completed').length}/${(project.milestones || []).length} done`}
                        size="small"
                        sx={{ height: 20, fontSize: 11, bgcolor: '#eef0fd', color: brand.primary, fontWeight: 600 }}
                      />
                    </Box>
                  </Box>

                  {/* Actions — side by side */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Visibility />}
                      onClick={() => handleViewProject(project)}
                      sx={{
                        flex: 1,
                        background: brand.gradient,
                        '&:hover': {
                          background: brand.gradientHover,
                        },
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      View Details
                    </Button>
                    {project.status !== 'completed' && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<NoteAdd />}
                        onClick={() => handleOpenDailyUpdateDialog(project)}
                        sx={{ flex: 1, textTransform: 'none', fontWeight: 600 }}
                      >
                        Add Daily Update
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Project Detail Dialog */}
      <Dialog
        open={showProjectDetail}
        onClose={handleCloseProjectDetail}
        maxWidth="lg"
        fullWidth
      >
        <DialogCloseButton onClick={handleCloseProjectDetail} />
        {selectedProject && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">{selectedProject.title}</Typography>
                <Chip
                  label={selectedProject.status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
                  color={getStatusColor(selectedProject.status)}
                  size="small"
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                {/* Description */}
                {selectedProject.description && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {selectedProject.description}
                    </Typography>
                  </Grid>
                )}

                {/* Project Details */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Location
                  </Typography>
                  <Typography variant="body1">
                    {selectedProject.location || 'Not specified'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Budget
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(selectedProject.budget)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Assigned Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedProject.assignedDate || selectedProject.created_at)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Status
                  </Typography>
                  <Chip
                    label={selectedProject.status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
                    color={getStatusColor(selectedProject.status)}
                    size="small"
                  />
                </Grid>

                {/* Project Owner Information */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Project Owner Information
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                    <Avatar>
                      {selectedProject.client?.name?.[0] || 'P'}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight="bold">
                        {selectedProject.client?.name || 'Unknown Project Owner'}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Chat />}
                      onClick={() => handleMessageUser(selectedProject.owner_id)}
                    >
                      Message
                    </Button>
                  </Box>
                </Grid>

                {/* Contractor next step */}
                <Grid item xs={12}>
                  {(() => {
                    const workflow = getStageWorkflowState(selectedProject);
                    return (
                      <Paper sx={{
                        p: 2.5,
                        borderRadius: 2,
                        bgcolor: '#f7fbfa',
                        border: `1px solid ${brand.primary}30`,
                      }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                          <Box sx={{ flex: 1 }}>
                            <Chip
                              label={workflow.stepLabel}
                              size="small"
                              sx={{ mb: 1, bgcolor: `${brand.primary}15`, color: brand.primary, fontWeight: 700 }}
                            />
                            <Typography variant="h6" sx={{ fontWeight: 800, color: text.primary, mb: 0.5 }}>
                              {workflow.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {workflow.description}
                            </Typography>
                          </Box>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <Button
                              variant="contained"
                              startIcon={workflow.primaryType === 'createStagePlan' ? <Assignment /> : <DescriptionIcon />}
                              onClick={() => handlePrimaryStageAction(selectedProject)}
                              sx={{ bgcolor: brand.primary, '&:hover': { bgcolor: brand.primary }, textTransform: 'none', fontWeight: 700 }}
                            >
                              {workflow.primaryAction}
                            </Button>
                            <Button
                              variant="outlined"
                              startIcon={<Chat />}
                              onClick={() => handleMessageUser(selectedProject.owner_id)}
                              sx={{ textTransform: 'none', fontWeight: 700 }}
                            >
                              Message Owner
                            </Button>
                          </Stack>
                        </Stack>
                      </Paper>
                    );
                  })()}
                </Grid>

                {/* Materials Schedule (BOQ) CTA */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Paper sx={{
                    p: 2, borderRadius: 2,
                    background: `linear-gradient(135deg, ${brand.primary}0A 0%, ${brand.primary}05 100%)`,
                    border: `1px solid ${brand.primary}25`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <DescriptionIcon sx={{ color: brand.primary, fontSize: 28 }} />
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: brand.primary }}>
                          Stage BOQ / Materials & Labour Schedule
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Build the materials, quantities, labour, and expected costs for each stage so the owner can approve before procurement.
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        setShowProjectDetail(false);
                        navigate(`/projects/${selectedProject.id}/boq`);
                      }}
                      sx={{
                        bgcolor: brand.primary, '&:hover': { bgcolor: brand.primary },
                        textTransform: 'none', fontWeight: 600
                      }}
                    >
                      Open BOQ
                    </Button>
                  </Paper>
                </Grid>

                {/* Work stages with report generator */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <MilestoneManagerWithReports
                    projectId={selectedProject.id}
                    projectContext={{
                      name: selectedProject.title,
                      type: selectedProject.project_type || selectedProject.type,
                      location: selectedProject.location,
                      description: selectedProject.description,
                    }}
                    userRole="contractor"
                    refreshKey={stageRefreshKey}
                    onCreateStagePlan={() => setShowManualMilestoneDialog(true)}
                    onOpenBOQ={() => {
                      setShowProjectDetail(false);
                      navigate(`/projects/${selectedProject.id}/boq`);
                    }}
                    onMilestoneCreated={(milestone) => {
                      // Reload project data to refresh milestones
                      handleViewProject(selectedProject);
                      setSuccess('Work stage created successfully!');
                    }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseProjectDetail}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Create Work Stage Menu */}
      <Menu
        anchorEl={createMilestoneMenuAnchor}
        open={Boolean(createMilestoneMenuAnchor)}
        onClose={handleCreateMilestoneMenuClose}
      >
        <MenuItem onClick={handleOpenAIMilestoneCreator}>
          <ListItemIcon>
            <AutoAwesome fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="AI Stage Plan Creator"
            secondary="Conversational AI interface"
          />
        </MenuItem>
        <MenuItem onClick={handleOpenManualMilestoneDialog}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Manual Stage Creation"
            secondary="Traditional form"
          />
        </MenuItem>
      </Menu>

      {/* AI Stage Plan Creator */}
      {selectedProject && (
        <AIMilestoneCreator
          open={showAIMilestoneCreator}
          onClose={() => setShowAIMilestoneCreator(false)}
          projectId={selectedProject.id}
          projectDetails={selectedProject}
          onComplete={handleMilestoneCreated}
        />
      )}

      {/* Manual Stage Creation Dialog */}
      {selectedProject && (
        <MilestoneCreationDialog
          open={showManualMilestoneDialog}
          onClose={() => setShowManualMilestoneDialog(false)}
          projectId={selectedProject.id}
          onMilestoneCreated={handleMilestoneCreated}
        />
      )}

      {dailyUpdateProject && (
        <AddDailyEntryDialog
          open={!!dailyUpdateProject}
          onClose={() => setDailyUpdateProject(null)}
          projectId={dailyUpdateProject.id}
          project={dailyUpdateProject}
          milestones={dailyUpdateProject.milestones || []}
          onEntryCreated={handleDailyEntryCreated}
        />
      )}
    </Container>
  );
};

export default ContractorProjectsPage;
